import { INNERSTYLES, OUTERSTYLES, SIZES } from "theme/components/buttons/buttonConfig";
import { above, media } from "core-web/utils/mediaqueries";

import BaseButton from "core-web/components/base/Button";
import Div from "theme/layouts/Div";
import Loading from "theme/components/Loading";
import PropTypes from "prop-types";
import React from "react";
import styleObjectToProps from "core-web/utils/styleObjectToProps";
import styled from "theme/libs/styled";
import transitions from "theme/config/transitions";
import useAboveBreakpoint from "core-web/hooks/useAboveBreakpoint";

export interface Theme {
    color?: string;
    backgroundColor?: string;
    backgroundColorHover?: string;
    borderColor?: string;
    opacity?: string;
    disabled?: Theme;
    active?: Theme;
    selected?: Theme;
    hover?: Theme & {
        opacity?: string;
    };
}

export interface Sizes {
    [key: string]: {
        [index: string]: string;
        // height: string;
        padding: string;
        textStyle: string;
    };
}

interface Props {
    as: string;
    children: Element;
    disableHover: boolean | null;
    isLoading: boolean | null;
    theme: Theme;
    type: string | null;
    size: any;
    height: string | string[] | null;
    padding: string | string[] | null;
}

const ignoredProps = ["buttonTheme", "active"];

const color = ({ buttonTheme: { color, opacity, backgroundColor, borderColor } }: { buttonTheme: Theme }) => `
    color: ${color || ""};
    opacity: ${opacity || "1"};
    background-color: ${backgroundColor || ""};
    border: 1px solid ${borderColor ? borderColor : "transparent"};
`;

// prettier-ignore
const disabled = ({ buttonTheme: { disabled } }: { buttonTheme: Theme }) => disabled ? `
    &:disabled, &.disabled {
        cursor: initial;
        color: ${disabled.color || ''};
        background-color: ${disabled.backgroundColor || ''};
        border-color: ${disabled.borderColor ? disabled.borderColor : 'none'};
        opacity: ${disabled.opacity || '1'};
    }
` : ``;

// prettier-ignore
const hover = ({ buttonTheme: { hover } }: { buttonTheme: Theme }) => hover ? `
    transition: all ${transitions.primary};

    .theme-btn-hover:hover &, &:hover:not(:disabled):not(.disabled) {
        opacity: ${hover.opacity || '1'};
        color: ${hover.color || ''};
        background-color: ${hover.backgroundColor || ''};
        ${hover.borderColor && `border-color: ${hover.borderColor}`};

        .primary { transform: translateY(-100%); opacity: 0; }
        .secondary { top: 0; opacity: 1; }
    }
` : ``;

// prettier-ignore
const active = ({ buttonTheme: { active } }: { buttonTheme: Theme }) => active ? `
    transition: all ${transitions.primary};

    &:active, &.active {
        opacity: ${active.opacity || '1'};
        color: ${active.color || ''};
        background-color: ${active.backgroundColor || ''};
        ${active.borderColor && `border-color: ${active.borderColor}`};
    }
` : ``;

// prettier-ignore
const selected = ({ buttonTheme: { selected } }: { buttonTheme: Theme }) => selected ? `
    transition: all ${transitions.primary};

    &.selected {
        opacity: ${selected.opacity || '1'};
        color: ${selected.color || ''};
        background-color: ${selected.backgroundColor || ''};
        ${selected.borderColor && `border-color: ${selected.borderColor}`};
    }
` : ``;

// prettier-ignore
const StyledButton = styled(BaseButton, {
    shouldForwardProp: (prop: string) => ignoredProps.indexOf(prop) === -1,
})`
    ${color}
    ${disabled}
    ${active}
    ${selected}

    ${above.tabletLg} {
        ${media.hover} {
            ${hover}
        }
    }

    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    &[data-is-loading='true'] > *:not(.loading) {
        opacity: 0;
    }

    .primary { transform: translateY(0); }
    .secondary {
        position: absolute;
        top: 100%;
        opacity: 0;
    }
`;

const Span = styled("span")``;

const mapSizeProps = (obj: Sizes, key: any, prop: string): string | string[] => {
    if (obj && key) {
        if (Array.isArray(key)) {
            return key.map((s: string) => (s && obj[s] ? obj[s][prop] : "")) as string[];
        }
        const sizeConfig = obj[key];
        return sizeConfig ? sizeConfig[prop] : "";
    }

    return "";
};

const Button = ({ as, children, disableHover, isLoading, theme, size, padding, type, ...rest }: Props) => {
    const btnPadding = padding || mapSizeProps(SIZES, size, "padding");
    const isDesktop = useAboveBreakpoint("tabletLg");

    if (disableHover) {
        delete theme["hover"];
    }

    return (
        <StyledButton
            as={as}
            type={type}
            buttonTheme={theme ?? {}}
            padding={btnPadding}
            data-is-loading={isLoading}
            disabled={isLoading}
            {...styleObjectToProps(mapSizeProps(SIZES, size, "textStyle"))}
            {...OUTERSTYLES}
            {...rest}
        >
            {isLoading && (
                <Loading className="loading" width="24px" height="24px" color="currentColor" position="absolute" />
            )}
            <Div position="relative">
                <Span className="primary" {...INNERSTYLES}>
                    {children}
                </Span>
                {isDesktop && !disableHover && (
                    <Span className="secondary" aria-hidden="true" {...INNERSTYLES}>
                        {children}
                    </Span>
                )}
            </Div>
        </StyledButton>
    );
};

const responsivePropType = PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]);

Button.propTypes = {
    alignItems: responsivePropType,
    as: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
    disableHover: PropTypes.bool,
    display: PropTypes.oneOfType([
        PropTypes.oneOf(["initial", "flex", "inline-flex", "none"]),
        PropTypes.arrayOf(PropTypes.oneOf(["initial", "flex", "inline-flex", "none"])),
    ]),
    height: responsivePropType,
    isLoading: PropTypes.bool,
    justifyContent: responsivePropType,
    padding: responsivePropType,
    size: responsivePropType,
    theme: PropTypes.object,
    type: PropTypes.string,
    width: responsivePropType,
};

Button.defaultProps = {
    alignItems: "center",
    as: "button",
    disableHover: false,
    display: "flex",
    height: null,
    isLoading: false,
    justifyContent: "center",
    padding: null,
    size: "lg",
    theme: null,
    type: "button",
    width: "fit-content",
};

export default Button;
